import { useEffect, useState } from 'react';
import { CpmButton, CpmModal, CpmTypeChip } from '../../../../components/design-system';
import {
    CPM_REJECT_REASON_OPTIONS,
    formatCpmCount,
    recordTypeLabel,
} from '../../../../constants/centralProductMaster.constants';
import { getRejectPreview, rejectQueueItem } from '../../../../services/productMasterService';
import type { CpmRejectReason } from '../../../../types/centralProductMaster.types';
interface RejectModalProps {
    open: boolean;
    level: 'brand' | 'product' | 'variant';
    masterId: string;
    name: string;
    recordType?: string;
    onClose: () => void;
    onRejected: () => void;
}

type Phase = 'ask' | 'working' | 'error' | 'done';

export default function RejectModal({
    open,
    level,
    masterId,
    name,
    recordType,
    onClose,
    onRejected,
}: RejectModalProps) {
    const [reason, setReason] = useState<CpmRejectReason | null>(null);
    const [descendantCount, setDescendantCount] = useState(0);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [phase, setPhase] = useState<Phase>('ask');

    useEffect(() => {
        if (!open) return;
        setReason(null);
        setPhase('ask');
        let cancelled = false;
        setLoadingPreview(true);
        getRejectPreview({ level, masterId })
            .then((data) => {
                if (!cancelled) setDescendantCount(data.descendantCount ?? 0);
            })
            .catch(() => {
                if (!cancelled) setDescendantCount(0);
            })
            .finally(() => {
                if (!cancelled) setLoadingPreview(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, level, masterId]);

    const cascadeText =
        !loadingPreview && descendantCount > 0
            ? `Rejecting this ${level} will also reject ${formatCpmCount(descendantCount)} still-pending descendant record${descendantCount === 1 ? '' : 's'} beneath it.`
            : '';

    const confirm = async () => {
        if (!reason) return;
        setPhase('working');
        try {
            await rejectQueueItem({ level, masterId, reason });
            setPhase('done');
            onRejected();
        } catch {
            setPhase('error');
        }
    };

    const finish = () => {
        setPhase('ask');
        setReason(null);
        onClose();
    };

    const typeLabel = recordType ? recordTypeLabel(recordType) : level;

    return (
        <CpmModal open={open} onClose={finish} maxWidth={490}>
            {phase === 'ask' ? (
                <>
                    <div
                        className="h-1"
                        style={{ background: 'linear-gradient(90deg,#D4183D,#F0577A)' }}
                    />
                    <div
                        className="flex items-start border-b border-[var(--cpm-border-default)]"
                        style={{ gap: 13, padding: '20px 22px 16px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                            style={{ width: 38, height: 38, borderRadius: 10 }}
                        >
                            <svg
                                width="19"
                                height="19"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <path d="m15 9-6 6M9 9l6 6" strokeLinecap="round" />
                            </svg>
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <div className="flex items-center flex-wrap" style={{ gap: 9 }}>
                                <span
                                    className="inline-flex items-center rounded-full bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)] uppercase"
                                    style={{
                                        padding: '3px 10px',
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Reject record
                                </span>
                                <CpmTypeChip recordType={recordType ?? level} />
                            </div>
                            <h2 className="m-0 !text-[18px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                {name}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={finish}
                            className="shrink-0 inline-flex items-center justify-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)]"
                            style={{ width: 30, height: 30, borderRadius: 8 }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                            >
                                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex flex-col" style={{ padding: '18px 22px 20px', gap: 14 }}>
                        <div className="flex flex-col" style={{ gap: 3 }}>
                            <span
                                className="text-[var(--cpm-text-primary)]"
                                style={{ fontSize: 13, fontWeight: 800, letterSpacing: '-0.01em' }}
                            >
                                Why reject this record?
                            </span>
                            <span
                                className="text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                            >
                                Pick the closest reason. It is recorded with the rejection.
                            </span>
                        </div>
                        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                            {CPM_REJECT_REASON_OPTIONS.map((opt) => {
                                const active = reason === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setReason(opt.value)}
                                        className="inline-flex items-center rounded-full transition-colors"
                                        style={{
                                            padding: '7px 13px',
                                            fontSize: 12.5,
                                            fontWeight: 800,
                                            border: `1.5px solid ${active ? 'var(--cpm-shortfall-text)' : 'var(--cpm-border-strong)'}`,
                                            background: active ? 'var(--cpm-shortfall-bg)' : '#fff',
                                            color: active
                                                ? 'var(--cpm-shortfall-text)'
                                                : 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        {cascadeText ? (
                            <div
                                className="flex items-start bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                                style={{ gap: 10, padding: '11px 13px', borderRadius: 10 }}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-shortfall-text)"
                                    strokeWidth="2"
                                    className="shrink-0 mt-px"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                                </svg>
                                <span
                                    className="text-[var(--cpm-shortfall-text)]"
                                    style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.5 }}
                                >
                                    {cascadeText} Those records would otherwise be left orphaned
                                    under a rejected parent.
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 12,
                            padding: '14px 22px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={finish}>
                                Cancel
                            </CpmButton>
                            <CpmButton
                                variant="danger"
                                size="modal"
                                onClick={confirm}
                                disabled={!reason}
                                leadingIcon={
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="m15 9-6 6M9 9l6 6" strokeLinecap="round" />
                                    </svg>
                                }
                            >
                                Reject this record
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}

            {phase === 'working' ? (
                <div
                    className="flex flex-col items-center text-center"
                    style={{ padding: '40px 30px 34px', gap: 14 }}
                >
                    <span className="cpm-spin" style={{ width: 34, height: 34, borderWidth: 3.4 }} />
                    <span
                        className="text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}
                    >
                        Rejecting record…
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)] max-w-[330px]"
                        style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                    >
                        Recording the reason and removing the record from the queue.
                    </span>
                </div>
            ) : null}

            {phase === 'error' ? (
                <>
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ padding: '34px 30px 22px', gap: 12 }}
                    >
                        <span
                            className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                            style={{ width: 52, height: 52, borderRadius: 14 }}
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                            Rejection failed
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[340px]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            The catalog service did not confirm the rejection. Nothing was changed.
                            Try again.
                        </span>
                    </div>
                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '14px 22px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={finish}>
                                Close
                            </CpmButton>
                            <CpmButton
                                variant="danger"
                                size="modal"
                                onClick={confirm}
                                leadingIcon={
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                }
                            >
                                Try again
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}

            {phase === 'done' ? (
                <div
                    className="flex flex-col items-center text-center"
                    style={{ padding: '40px 30px 30px', gap: 12 }}
                >
                    <span
                        className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                        style={{ width: 52, height: 52, borderRadius: 14 }}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="m15 9-6 6M9 9l6 6" strokeLinecap="round" />
                        </svg>
                    </span>
                    <span
                        className="text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}
                    >
                        Rejected
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            lineHeight: 1.5,
                            maxWidth: 360,
                        }}
                    >
                        “{name}” ({typeLabel}) has been rejected and removed from the queue.
                        {cascadeText ? ` ${cascadeText}` : ''}
                    </span>
                    <CpmButton variant="navy" size="md" className="mt-1.5" onClick={finish}>
                        Back to the queue
                    </CpmButton>
                </div>
            ) : null}
        </CpmModal>
    );
}
